import { useEffect, useState } from 'react';
import { readOverview } from '../api';
import type { OverviewItem } from '../api-types';
import { getDriveToken } from '../drive-token';
import type { DriveChildren } from './hotspots';

export type HotspotsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly lists: readonly DriveChildren[] };

/**
 * One `GET /v1/archive/overview?path=<drive>` per visible drive, for the work list of the root.
 * No drive, no call. A drive that fails is left out; the root's own 428 never gets here.
 */
export function useHotspots(drives: readonly OverviewItem[]): HotspotsState {
  const [state, setState] = useState<HotspotsState>({ status: 'loading' });
  const key = drives.map((drive) => drive.id).join('|');

  useEffect(() => {
    if (drives.length === 0) {
      setState({ status: 'ready', lists: [] });
      return;
    }
    let live = true;
    void Promise.all(
      drives.map(async (drive) => {
        const answer = await readOverview(drive.id, getDriveToken());
        return answer.ok ? { drive, items: answer.value.items } : null;
      }),
    ).then((lists) => {
      if (live) setState({ status: 'ready', lists: lists.filter((list) => list !== null) });
    });
    return () => {
      live = false;
    };
    // The drives' ids are the key: a silent refresh of the same drives does not refetch.
  }, [key]);

  return state;
}
